import { reactive } from 'vue'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  duration?: number
  persistent?: boolean
}

interface ToastState {
  toasts: Toast[]
}

const state = reactive<ToastState>({
  toasts: []
})

let toastIdCounter = 0

function generateToastId(): string {
  return `toast-${++toastIdCounter}-${Date.now()}`
}

export function useToast() {
  const showToast = (options: Omit<Toast, 'id'>) => {
    const toast: Toast = {
      id: generateToastId(),
      duration: 4000, // Default 4 seconds
      ...options
    }

    state.toasts.push(toast)

    // Auto-remove after duration (unless persistent)
    if (!toast.persistent && toast.duration && toast.duration > 0) {
      setTimeout(() => {
        removeToast(toast.id)
      }, toast.duration)
    }

    return toast.id
  }

  const removeToast = (id: string) => {
    const index = state.toasts.findIndex(toast => toast.id === id)
    if (index > -1) {
      state.toasts.splice(index, 1)
    }
  }

  const clearAllToasts = () => {
    state.toasts.splice(0)
  }

  // Convenience methods
  const success = (message: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'message'>>) => {
    return showToast({ type: 'success', message, ...options })
  }

  const error = (message: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'message'>>) => {
    return showToast({ type: 'error', message, duration: 6000, ...options }) // Errors stay longer
  }

  const warning = (message: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'message'>>) => {
    return showToast({ type: 'warning', message, ...options })
  }

  const info = (message: string, options?: Partial<Omit<Toast, 'id' | 'type' | 'message'>>) => {
    return showToast({ type: 'info', message, ...options })
  }

  return {
    toasts: state.toasts,
    showToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info
  }
}